// src/Components/Device/ControlPanel.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Spinner } from 'react-bootstrap';
import {
  FaMapMarkerAlt, FaArrowLeft, FaWater,
  FaCogs, FaServer, FaFingerprint, FaBolt, FaThermometerHalf
} from 'react-icons/fa';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

import { fetchDeviceByCode } from '../../redux/deviceActions';
import { fetchSensorsForDevice } from '../../redux/sensorActions';
import WaterLevelSensorPage from './WaterLevelSensor/WaterLevelSensorPage';
import WaterLevelControllerPage from './WaterLevelSensor/WaterLevelControllerPage';
import MotorStatusBox from './WaterLevelSensor/MotorStatusBox';
import VirtualMotorsGrid from './WaterLevelSensor/VirtualMotorsGrid';
import VirtualMotorStatusBox from './WaterLevelSensor/VirtualMotorStatusBox';
import SequencerStatusBox from './WaterLevelSensor/SequencerStatusBox';
import WaterLevelFunctionalSettingsDialog from './WaterLevelSensor/WaterLevelFunctionalSettingsDialog';
import WaterLevelAlarmSettingsDialog from './WaterLevelSensor/WaterLevelAlarmSettingsDialog';
import CurrentSensorDashboard from './sensors/CurrentSensorDashboard';
import TemperatureSensorDashboard from './sensors/TemperatureSensorDashboard';
import { API_BASE_URL } from '../../config/config';

/* ── Shared card style ── */
const cardStyle = {
  borderRadius: 12,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06)',
  border: 'none',
  background: '#fff',
};

const ControlPanel = () => {
  const { deviceId } = useParams();
  const dispatch     = useDispatch();
  const navigate     = useNavigate();

  const [userPermission,       setUserPermission]       = useState(null);
  const [selectedTab,          setSelectedTab]          = useState('sensor');
  const [controllerData,       setControllerData]       = useState(null);
  const [showFunctionalDialog, setShowFunctionalDialog] = useState(false);
  const [showAlarmDialog,      setShowAlarmDialog]      = useState(false);
  const [selectedSensor,       setSelectedSensor]       = useState(null);
  const [virtualMotors,        setVirtualMotors]        = useState([]);

  const devices       = useSelector((s) => s.device.devices) || [];
  const devicesLoading = useSelector((s) => s.device.loading);
  const devicesError   = useSelector((s) => s.device.error);
  const sensors       = useSelector((s) => s.sensor.sensors) || [];
  const currentDevice = devices.find((d) => d.deviceId === deviceId);

  // Same condition Aquabrim uses to expose virtual motors / groups
  const isVirtual = currentDevice?.virtual && currentDevice?.device_type === 'Water level controller';

  // Every tab is surfaced only when the device actually carries that kind of
  // sensor/controller. Computed as primitives so effects/tabs stay stable.
  const currentSensor = sensors.find((s) => s.sensor_type === 'Current Sensor');
  const temperatureSensors = sensors.filter((s) => s.sensor_type === 'Temperature Sensor');
  const hasCurrentSensor = Boolean(currentSensor);
  const hasTemperatureSensor = temperatureSensors.length > 0;
  // The "Sensor" tab is the Water Level view — only meaningful when the device
  // has water-level sensors (otherwise it renders an empty table).
  const hasWaterLevelSensor = sensors.some((s) => s.sensor_type === 'Water Level Sensor');
  // Virtual devices expose Motors/Groups instead of a physical controller, so
  // the Controller tab is hidden for them (matches Aquabrim).
  const showController = !isVirtual && controllerData && controllerData.subId !== '00';

  // Ordered list of tab keys available for this device.
  const availableTabs = useMemo(
    () => [
      ...(isVirtual ? ['motors'] : []),
      ...(hasWaterLevelSensor ? ['sensor'] : []),
      ...(hasCurrentSensor ? ['current-sensor'] : []),
      ...(hasTemperatureSensor ? ['temperature-sensor'] : []),
      ...(showController ? ['controller'] : []),
    ],
    [isVirtual, hasWaterLevelSensor, hasCurrentSensor, hasTemperatureSensor, showController],
  );

  // Preferred landing tab: always a sensor/controller view first — Motors is
  // only used as a last resort so a device never opens on the Motors tab by
  // default when it has any sensor to show.
  const defaultTab =
    (hasWaterLevelSensor && 'sensor') ||
    (hasCurrentSensor && 'current-sensor') ||
    (hasTemperatureSensor && 'temperature-sensor') ||
    (showController && 'controller') ||
    (isVirtual && 'motors') ||
    null;

  // Fetch virtual motors for this device (mirrors Aquabrim's ControlPanel)
  const fetchVirtualMotors = async (deviceCode) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}api/virtual/motor/${deviceCode}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setVirtualMotors(response.data.motors || []);
      }
    } catch (error) {
      console.error('Error fetching virtual motors:', error);
      setVirtualMotors([]);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const decoded = jwtDecode(token);
    setUserPermission(decoded.permission);
    // Resolve THIS device directly by code so a hard refresh / direct link works
    // regardless of which paginated page the device would fall on in the list.
    if (deviceId) dispatch(fetchDeviceByCode(deviceId));
  }, [dispatch, deviceId]);

  useEffect(() => {
    if (!currentDevice) return;
    dispatch(fetchSensorsForDevice(currentDevice.deviceId));
    axios.get(`${API_BASE_URL}api/sensor/${deviceId}/00`)
      .then(r => setControllerData(r.data.sensor))
      .catch(e => console.error(e));

    // Virtual devices expose motors/groups instead of a physical controller.
    // Note: we intentionally do NOT switch the active tab — the Sensor tab
    // stays the default landing view every time a device is opened.
    if (isVirtual) {
      fetchVirtualMotors(currentDevice.deviceId);
    }
  }, [currentDevice, dispatch, isVirtual]);

  // Keep the active tab valid for this device. If the current selection isn't
  // among the available tabs (e.g. the default "sensor" on a device with only
  // current/temperature sensors, or after switching devices), snap to the first
  // available tab. Waits until at least one tab is known so we don't thrash
  // during the initial sensor fetch.
  useEffect(() => {
    if (availableTabs.length && !availableTabs.includes(selectedTab)) {
      setSelectedTab(defaultTab || availableTabs[0]);
    }
  }, [availableTabs, defaultTab, selectedTab]);

  const handleFunctionalOpen = (s) => { setSelectedSensor(s); setShowFunctionalDialog(true); };
  const handleAlarmOpen      = (s) => { setSelectedSensor(s); setShowAlarmDialog(true); };
  const handleDialogClose    = () => {
    setShowFunctionalDialog(false);
    setShowAlarmDialog(false);
    setSelectedSensor(null);
  };

  /* ── Loading vs. Not found ──
     Show the spinner only while the device list is actually being fetched (or
     hasn't been requested yet). Once loading is finished and the device still
     isn't found, stop spinning and show an actionable message — otherwise a
     hard refresh on a missing/unknown device would spin forever. */
  if (!currentDevice) {
    const stillLoading = devicesLoading || (devices.length === 0 && !devicesError);

    if (stillLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center"
          style={{ height: '100vh', background: '#f8fafc' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary"
              style={{ width: '2.5rem', height: '2.5rem' }} />
            <p className="mt-3 fw-semibold"
              style={{ fontSize: '0.82rem', color: '#64748b' }}>
              Connecting to device...
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="d-flex justify-content-center align-items-center"
        style={{ height: '100vh', background: '#f8fafc' }}>
        <div className="text-center px-3">
          <p className="fw-bold mb-1" style={{ color: '#0f172a' }}>
            Device not found
          </p>
          <p className="mb-3" style={{ fontSize: '0.82rem', color: '#64748b' }}>
            We couldn't find this device for your account.
          </p>
          <button
            onClick={() => navigate('/manage-device')}
            style={{
              border: '1px solid #1976d2', background: '#1976d2', color: '#fff',
              borderRadius: 8, padding: '8px 18px', fontWeight: 600, cursor: 'pointer'
            }}
          >
            Back to Devices
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ── Global styles for info bar + animations ── */}
      <style>{`
        @keyframes cpulse {
          0%,100% { transform: scale(1);   opacity: 0.4; }
          50%      { transform: scale(2.4); opacity: 0;   }
        }

        /* ─── MOBILE LAYOUT (Grid) ─── */
        .info-bar {
          display: grid;
          grid-template-columns: 1fr 1fr; /* Split top into 2 columns */
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          /* Optional: Add a subtle border around the whole block */
          border: 1px solid #f1f5f9; 
        }

        /* Top Row: ID and Location share space */
        .ib-id       { grid-column: span 1; border-right: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; }
        .ib-location { grid-column: span 1; border-bottom: 1px solid #f1f5f9; }

        /* Middle & Bottom Rows: FULL WIDTH on Mobile */
        .ib-motor    { grid-column: span 2; border-bottom: 1px solid #f1f5f9; } 
        .ib-status   { grid-column: span 2; }

        /* Generic Cell Styling */
        .ib-cell {
          padding: 12px 16px;
          display: flex;
          align-items: center;
          min-height: 70px;
        }

        /* Specific tweaks */
        .ib-motor { 
          padding: 16px; 
          justify-content: center; 
          width: 100%;
        }

        /* ─── DESKTOP LAYOUT (Flex) ─── */
        @media (min-width: 768px) {
          .info-bar {
            display: flex;
            flex-direction: row;
            align-items: stretch;
            border: none; /* Let Card handle border on desktop */
          }

          /* Reset Grid Properties */
          .ib-id, .ib-location, .ib-motor, .ib-status {
            grid-column: auto;
            border-bottom: none;
            border-right: 1px solid #f1f5f9;
          }
          /* Remove right border from last item */
          .ib-status { border-right: none; }

          /* Flex Sizing */
          .ib-id       { flex: 0 0 160px; }
          .ib-location { flex: 0 0 180px; }
          .ib-motor    { flex: 1; } /* Motor takes remaining space */
          .ib-status   { flex: 0 0 170px; }
        }

        /* Icon styling */
        .ib-icon {
          width: 36px; height: 36px; border-radius: 10px;
          background: #f8fafc; border: 1px solid #e2e8f0;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          margin-right: 12px;
        }

        /* Tab button hover */
        .cp-tab-btn:hover { background: #f1f5f9 !important; }
        .cp-tab-btn.cp-active:hover { background: #1e293b !important; }
      `}</style>

      <div style={{ minHeight: '100vh', background: '#f8fafc' }}
        className="px-3 px-md-4 py-3">

        {/* ══════════════════════════════════════════
            PAGE HEADER — Back + Title + Tab Toggle
        ══════════════════════════════════════════ */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
          paddingTop: '12px'
        }}>

          {/* Back + Device Name */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: '1 1 auto',
            minWidth: 0
          }}>
            <button
              onClick={() => navigate(-1)}
              style={{
                width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                border: '1px solid #e2e8f0', background: '#fff',
                cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <FaArrowLeft size={12} color="#64748b" />
            </button>

            <div style={{ minWidth: 0 }}>
              <h5 className="fw-bold mb-0" style={{ fontSize: '0.95rem', color: '#0f172a', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentDevice.deviceName}
              </h5>
              <span style={{
                fontSize: '0.68rem', color: '#94a3b8',
                display: 'flex', alignItems: 'center', gap: 4
              }}>
                <FaServer size={9} /> Control Panel
              </span>
            </div>
          </div>

          {/* Tab Toggle */}
          <div style={{
            display: 'flex', gap: 2, padding: 3,
            background: '#fff', borderRadius: 10,
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            flexShrink: 0
          }}>
            {[
              ...(isVirtual
                ? [{ key: 'motors', label: 'Motors', icon: <FaBolt size={10} /> }]
                : []),
              ...(hasWaterLevelSensor
                ? [{ key: 'sensor', label: 'Sensor', icon: <FaWater size={10} /> }]
                : []),
              ...(hasCurrentSensor
                ? [{ key: 'current-sensor', label: 'Current', icon: <FaBolt size={10} /> }]
                : []),
              ...(hasTemperatureSensor
                ? [{ key: 'temperature-sensor', label: 'Temperature', icon: <FaThermometerHalf size={10} /> }]
                : []),
              ...(showController
                ? [{ key: 'controller', label: 'Controller', icon: <FaCogs size={10} /> }]
                : [])
            ].map(t => (
              <button
                key={t.key}
                className={`cp-tab-btn${selectedTab === t.key ? ' cp-active' : ''}`}
                onClick={() => setSelectedTab(t.key)}
                style={{
                  border: 'none', outline: 'none', cursor: 'pointer',
                  borderRadius: 8, padding: '6px 14px',
                  fontSize: '0.78rem', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 5,
                  transition: 'all 0.15s',
                  background: selectedTab === t.key ? '#0f172a' : 'transparent',
                  color:      selectedTab === t.key ? '#fff'    : '#94a3b8',
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════════
            MOTORS TAB (Virtual devices)
        ══════════════════════════════════════════ */}
        {selectedTab === 'motors' && isVirtual && (
          <Card style={cardStyle}>
            <Card.Body className="p-2 p-md-3">
              <VirtualMotorsGrid
                virtualMotors={virtualMotors}
                deviceCode={currentDevice.deviceId}
                fetchVirtualMotors={fetchVirtualMotors}
              />
            </Card.Body>
          </Card>
        )}

        {/* ══════════════════════════════════════════
            CURRENT SENSOR TAB
        ══════════════════════════════════════════ */}
        {selectedTab === 'current-sensor' && hasCurrentSensor && (
          <CurrentSensorDashboard sensor={currentSensor} />
        )}

        {/* ══════════════════════════════════════════
            TEMPERATURE SENSOR TAB
        ══════════════════════════════════════════ */}
        {selectedTab === 'temperature-sensor' && hasTemperatureSensor && (
          <TemperatureSensorDashboard sensors={temperatureSensors} />
        )}

        {/* ══════════════════════════════════════════
            SENSOR TAB
        ══════════════════════════════════════════ */}
        {selectedTab === 'sensor' && hasWaterLevelSensor && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* ── INFO BAR CARD ── */}
            <Card style={cardStyle}>
              <div className="info-bar">

                {/* 1. Device ID */}
                <div className="ib-id ib-cell">
                  <div className="ib-icon">
                    <FaFingerprint size={14} color="#64748b" />
                  </div>
                  <div>
                    <p className="mb-0 text-uppercase" style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.05em' }}>
                      Device ID
                    </p>
                    <p className="mb-0 text-dark" style={{ fontSize: '0.9rem', fontWeight: 700 }}>
                      {currentDevice.deviceId}
                    </p>
                  </div>
                </div>

                {/* 2. Location */}
                <div className="ib-location ib-cell">
                  <div className="ib-icon">
                    <FaMapMarkerAlt size={14} color="#64748b" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p className="mb-0 text-uppercase" style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.05em' }}>
                      Location
                    </p>
                    <p className="mb-0 text-dark text-truncate" style={{ fontSize: '0.9rem', fontWeight: 700 }}>
                      {currentDevice.site_detail || 'Level Based'}
                    </p>
                  </div>
                </div>

                {/* 3. Motor Status - Full Width on Mobile.
                    Virtual devices: sequencer logic box(es), else a single motor box.
                    Multiple plain motors live in the Motors tab. Physical devices use MotorStatusBox. */}
                <div className="ib-motor ib-cell">
                  {isVirtual ? (
                    virtualMotors.filter(m => m.executionType === 'sequencer').length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, width: '100%', justifyContent: 'center' }}>
                        {virtualMotors
                          .filter(m => m.executionType === 'sequencer')
                          .map(motor => (
                            <div key={motor._id} style={{ flex: 1, minWidth: 280, maxWidth: 400 }}>
                              <SequencerStatusBox
                                deviceCode={currentDevice.deviceId}
                                motor={motor}
                                allVirtualMotors={virtualMotors}
                                onRefresh={() => fetchVirtualMotors(currentDevice.deviceId)}
                              />
                            </div>
                          ))}
                      </div>
                    ) : virtualMotors.length === 1 ? (
                      <VirtualMotorStatusBox
                        key={virtualMotors[0]._id}
                        deviceCode={currentDevice.deviceId}
                        motor={virtualMotors[0]}
                        onRefresh={() => fetchVirtualMotors(currentDevice.deviceId)}
                        hasCommError={currentDevice.activeErrors?.includes('commError') || false}
                      />
                    ) : (
                      <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>
                        Open the <strong style={{ color: '#64748b' }}>Motors</strong> tab to control motors
                      </span>
                    )
                  ) : controllerData ? (
                    <div style={{ width: '100%' }}>
                      <MotorStatusBox
                        motorData={controllerData.controllerData}
                        userPermission={userPermission}
                        deviceCode={currentDevice.deviceId}
                      />
                    </div>
                  ) : (
                    <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>
                      Motor status unavailable
                    </span>
                  )}
                </div>

                {/* 4. Online Status */}
                <div className="ib-status ib-cell">
                  <div style={{ position: 'relative', width: 12, height: 12, marginRight: 12 }}>
                    <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#22c55e' }} />
                    <div style={{
                      position: 'absolute', inset: -4, borderRadius: '50%',
                      border: '2px solid #22c55e', opacity: 0.3,
                      animation: 'cpulse 2s ease-in-out infinite'
                    }} />
                  </div>
                  <div>
                    <p className="mb-0 text-uppercase" style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.05em' }}>
                      Status
                    </p>
                    <p className="mb-0 text-dark" style={{ fontSize: '0.9rem', fontWeight: 700 }}>
                      Online · Stable
                    </p>
                  </div>
                </div>

              </div>
            </Card>

            {/* ── SENSOR CARDS + GRAPH ── */}
            <Card style={cardStyle}>
              <Card.Body className="p-3 p-md-4">
                <WaterLevelSensorPage
                  currentDevice={currentDevice}
                  sensorsData={sensors.filter(s => s.sensor_type === 'Water Level Sensor')}
                  onOpenFunctionalDialog={handleFunctionalOpen}
                  onOpenAlarmDialog={handleAlarmOpen}
                  refreshSensors={() => dispatch(fetchSensorsForDevice(currentDevice.deviceId))}
                  motorStatusData={controllerData}
                  userPermission={userPermission}
                />
              </Card.Body>
            </Card>

          </div>
        )}

        {/* ══════════════════════════════════════════
            CONTROLLER TAB
        ══════════════════════════════════════════ */}
        {selectedTab === 'controller' && (
          <Card style={cardStyle}>
            <Card.Body className="p-3 p-md-4">
              <WaterLevelControllerPage
                controllerData={controllerData}
                device={currentDevice}
                sensors={sensors}
                userPermission={userPermission}
              />
            </Card.Body>
          </Card>
        )}

        {/* ── Dialogs ── */}
        <WaterLevelFunctionalSettingsDialog
          open={showFunctionalDialog}
          onClose={handleDialogClose}
          sensorsData={selectedSensor ? [selectedSensor] : []}
          deviceCode={selectedSensor?.device_code || ''}
          subId={selectedSensor?.sensor_sub_id || ''}
          onUpdateSuccess={() => dispatch(fetchSensorsForDevice(currentDevice.deviceId))}
        />
        <WaterLevelAlarmSettingsDialog
          open={showAlarmDialog}
          onClose={handleDialogClose}
          sensorsData={selectedSensor ? [selectedSensor] : []}
          deviceCode={selectedSensor?.device_code || ''}
          subId={selectedSensor?.sensor_sub_id || ''}
          onUpdateSuccess={() => dispatch(fetchSensorsForDevice(currentDevice.deviceId))}
        />

      </div>
    </>
  );
};

export default ControlPanel;